import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Grid,
  styled,
  useTheme,
  Button,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CancelIcon from "@mui/icons-material/Cancel";
import ImageIcon from "@mui/icons-material/Image";
import AddIcon from "@mui/icons-material/Add";
import { tokens } from "../theme";
import type {
  FileWithPreview,
  FileUploadSectionProps,
} from "../types/file-upload-section";

interface ToastNotification {
  error: (message: string) => void;
}

interface EnhancedFileUploadSectionProps extends FileUploadSectionProps {
  toast?: ToastNotification;
}

// Styled components moved outside to avoid recreation on every render
const UploadBox = styled(Paper)(({ theme }) => ({
  border: "2px dashed #666",
  borderRadius: 4,
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  height: "100%",
  minHeight: 150,
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: "#333",
    backgroundColor: "#f5f5f5",
  },
  "&:active": {
    transform: "scale(0.98)",
  },
}));

const AddMoreBox = styled(Paper)(({ theme }) => ({
  border: "2px dashed #999",
  borderRadius: 4,
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  height: "100%",
  minHeight: 150,
  transition: "all 0.3s ease",
  backgroundColor: "#fafafa",
  "&:hover": {
    borderColor: "#666",
    backgroundColor: "#f0f0f0",
  },
}));

const PreviewBox = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: 150,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  border: "1px solid #ccc",
  borderRadius: 4,
  backgroundColor: "#F3F4F6",
}));

const PreviewImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const DeleteButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: 4,
  right: 4,
  backgroundColor: "rgba(255, 255, 255, 0.7)",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.9)",
  },
}));

const FileUploadSection: React.FC<EnhancedFileUploadSectionProps> = ({
  title,
  maxFiles = 5, // Updated default to 5
  acceptedFileTypes = "image/*",
  onFilesChange,
  existingFiles = [],
  onExistingFilesChange,
  currentFiles = [],
  toast,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [existingImageUrls, setExistingImageUrls] =
    useState<string[]>(existingFiles);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const addMoreInputRef = useRef<HTMLInputElement>(null); // Added ref for "add more" input

  const validateFileSize = (
    files: File[]
  ): { validFiles: File[]; oversizedFiles: File[] } => {
    const maxSizeInBytes = 2 * 1024 * 1024; // 2MB
    const validFiles: File[] = [];
    const oversizedFiles: File[] = [];

    files.forEach((file) => {
      if (file.size <= maxSizeInBytes) {
        validFiles.push(file);
      } else {
        oversizedFiles.push(file);
      }
    });

    return { validFiles, oversizedFiles };
  };

  // Update existing images when prop changes
  React.useEffect(() => {
    setExistingImageUrls(existingFiles);
  }, [existingFiles]);

  // Sync internal files state with external files
  React.useEffect(() => {
    if (currentFiles && currentFiles.length > 0) {
      const filesWithPreviews = currentFiles.map((file) => {
        return Object.assign(file, {
          preview: URL.createObjectURL(file),
        });
      });
      setFiles(filesWithPreviews);
    }
  }, [currentFiles]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const selectedFiles = Array.from(event.target.files || []);

      if (selectedFiles.length === 0) {
        return;
      }

      const { validFiles, oversizedFiles } = validateFileSize(selectedFiles);

      if (oversizedFiles.length > 0) {
        const errorMessage = `${
          oversizedFiles.length
        } file(s) exceed the 2MB limit and were not uploaded: ${oversizedFiles
          .map((f) => f.name)
          .join(", ")}`;
        if (toast) {
          toast.error(errorMessage);
        } else {
          alert(errorMessage);
        }
      }

      if (validFiles.length === 0) {
        return;
      }

      if (validFiles.length + files.length > maxFiles) {
        const errorMessage = `You can only upload up to ${maxFiles} files.`;
        if (toast) {
          toast.error(errorMessage);
        } else {
          alert(errorMessage);
        }
        return;
      }

      const newFiles = validFiles.map((file) => {
        return Object.assign(file, {
          preview: URL.createObjectURL(file),
        });
      });

      const updatedFiles = [...files, ...newFiles].slice(0, maxFiles);
      setFiles(updatedFiles);

      if (onFilesChange) {
        onFilesChange(updatedFiles);
      }

      // Reset the input value to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (addMoreInputRef.current) {
        addMoreInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error handling file change:", error);
      const errorMessage = "Error uploading files. Please try again.";
      if (toast) {
        toast.error(errorMessage);
      } else {
        alert(errorMessage);
      }
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    try {
      event.preventDefault();
      event.stopPropagation();

      if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
        const droppedFiles = Array.from(event.dataTransfer.files);

        const { validFiles, oversizedFiles } = validateFileSize(droppedFiles);

        if (oversizedFiles.length > 0) {
          const errorMessage = `${
            oversizedFiles.length
          } file(s) exceed the 2MB limit and were not uploaded: ${oversizedFiles
            .map((f) => f.name)
            .join(", ")}`;
          if (toast) {
            toast.error(errorMessage);
          } else {
            alert(errorMessage);
          }
        }

        if (validFiles.length === 0) {
          return;
        }

        if (validFiles.length + files.length > maxFiles) {
          const errorMessage = `You can only upload up to ${maxFiles} files.`;
          if (toast) {
            toast.error(errorMessage);
          } else {
            alert(errorMessage);
          }
          return;
        }

        const newFiles = validFiles.map((file) => {
          return Object.assign(file, {
            preview: URL.createObjectURL(file),
          });
        });

        const updatedFiles = [...files, ...newFiles].slice(0, maxFiles);
        setFiles(updatedFiles);

        if (onFilesChange) {
          onFilesChange(updatedFiles);
        }
      }
    } catch (error) {
      console.error("Error handling file drop:", error);
      const errorMessage = "Error uploading files. Please try again.";
      if (toast) {
        toast.error(errorMessage);
      } else {
        alert(errorMessage);
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = [...files];
    URL.revokeObjectURL(updatedFiles[index].preview);
    updatedFiles.splice(index, 1);
    setFiles(updatedFiles);

    if (onFilesChange) {
      onFilesChange(updatedFiles);
    }
  };

  const handleRemoveExistingImage = (index: number) => {
    const updatedExistingFiles = [...existingImageUrls];
    updatedExistingFiles.splice(index, 1);
    setExistingImageUrls(updatedExistingFiles);
    if (onExistingFilesChange) {
      onExistingFilesChange(updatedExistingFiles);
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    } else {
      console.error("File input ref is null");
    }
  };

  const handleAddMoreClick = () => {
    if (addMoreInputRef.current) {
      addMoreInputRef.current.click();
    } else {
      console.error("Add more file input ref is null");
    }
  };

  React.useEffect(() => {
    return () => {
      files.forEach((file) => URL.revokeObjectURL(file.preview));
    };
  }, []);

  const renderPreviewBoxes = () => {
    const boxes = [];
    const totalImages = existingImageUrls.length + files.length;

    // Render existing images first
    existingImageUrls.forEach((imageUrl, index) => {
      boxes.push(
        <Grid size={{ xs: 12, sm: 4 }} key={`existing-${index}`}>
          <PreviewBox>
            <PreviewImage
              src={imageUrl}
              alt={`Existing image ${index + 1}`}
              onError={(e) => {
                console.error(`Failed to load image: ${imageUrl}`);
                e.currentTarget.style.display = "none";
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                bottom: 4,
                left: 4,
                backgroundColor: "rgba(255,255,255,0.7)",
                padding: "2px 4px",
                borderRadius: 1,
                fontSize: "0.6rem",
              }}
            >
              Existing Image {index + 1}
            </Typography>
            <DeleteButton
              size="small"
              onClick={() => handleRemoveExistingImage(index)}
            >
              <CancelIcon fontSize="small" />
            </DeleteButton>
          </PreviewBox>
        </Grid>
      );
    });

    // Render uploaded files
    files.forEach((file, index) => {
      boxes.push(
        <Grid size={{ xs: 12, sm: 4 }} key={`file-${index}`}>
          <PreviewBox>
            <PreviewImage src={file.preview} alt={file.name} />
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                bottom: 4,
                left: 4,
                backgroundColor: "rgba(255,255,255,0.7)",
                padding: "2px 4px",
                borderRadius: 1,
                fontSize: "0.6rem",
              }}
            >
              {file.name}
            </Typography>
            <DeleteButton size="small" onClick={() => handleRemoveFile(index)}>
              <CancelIcon fontSize="small" />
            </DeleteButton>
          </PreviewBox>
        </Grid>
      );
    });

    if (totalImages < maxFiles) {
      // Render placeholder boxes for remaining slots up to maxFiles
      for (let i = totalImages; i < maxFiles; i++) {
        boxes.push(
          <Grid size={{ xs: 12, sm: 4 }} key={`placeholder-${i}`}>
            <PreviewBox>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  color: "text.secondary",
                }}
              >
                <ImageIcon
                  sx={{
                    fontSize: 30,
                    opacity: 0.3,
                    color: colors.primary[300],
                  }}
                />
              </Box>
            </PreviewBox>
          </Grid>
        );
      }
    } else {
      boxes.push(
        <Grid size={{ xs: 12, sm: 4 }} key="add-more">
          <AddMoreBox onClick={handleAddMoreClick}>
            <AddIcon
              sx={{ fontSize: 38, color: colors.primary[200], mb: 0.5 }}
            />
            <Typography variant="subtitle2" align="center">
              Add More
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: colors.primary[300] }}
              align="center"
            >
              Upload additional images
            </Typography>
            <input
              ref={addMoreInputRef}
              type="file"
              multiple
              accept={acceptedFileTypes}
              onChange={handleFileChange}
              style={{ display: "none" }}
              data-testid="add-more-file-input"
            />
          </AddMoreBox>
        </Grid>
      );
    }

    return boxes;
  };

  return (
    <Box sx={{ mb: 2, backgroundColor: colors.primary[800], p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Grid container spacing={1}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <UploadBox
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={handleUploadClick}
          >
            <CloudUploadIcon
              sx={{ fontSize: 38, color: colors.primary[100], mb: 0.5 }}
            />
            <Typography variant="subtitle2" align="center">
              Drag & Drop
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: colors.primary[300] }}
              align="center"
            >
              Or select files
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleUploadClick();
              }}
              sx={{ mt: 1, color: colors.primary[100] }}
            >
              Browse Files
            </Button>
            <Typography variant="caption" sx={{ color: "red" }} align="center">
              Maximum file size: 2MB
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={acceptedFileTypes}
              onChange={handleFileChange}
              style={{ display: "none" }}
              data-testid="file-input"
            />
          </UploadBox>
        </Grid>
        {renderPreviewBoxes()}
      </Grid>
    </Box>
  );
};

export default FileUploadSection;
